/**
 * E-Mails: Liste aller synchronisierten Threads, rechts der gewaehlte Thread.
 *
 * EmailThreadList und EmailThreadDetail gab es seit Monaten, eingehaengt waren
 * sie bis zum 05.09.2026 nirgends. Damit war der Anhang-Download aus
 * EmailThreadDetail (docs/it/plan-auth-luecke.md) an keiner Stelle klickbar.
 *
 * Deep-Link: /emails/:threadId — aus der Historie am Kontakt und am Kunden.
 *
 * Filter stehen in der URL (?customer_id=…&archiviert=1), damit ein Link mit
 * Filter teilbar ist und die Auswahl beim Zurueck-Navigieren bleibt. Das
 * Backend (GET /emails) kann nach Kunde, Kontakt, Projekt und Archiv filtern;
 * eine Volltextsuche nach Betreff oder Absender gibt es dort nicht, deshalb
 * fehlt sie hier bewusst.
 */

import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { Card, Combobox, EmptyState, NativeCheckbox, Pagination } from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { useAuswahlSuche } from '@/hooks/useAuswahlSuche';
import { useEmailThreads, useEmailThread } from '@/hooks/useEmailThreads';
import { EmailThreadList, EmailThreadDetail } from './components';

const PER_PAGE = 25;

export function EmailsPage() {
  const navigate = useNavigate();
  const { threadId } = useParams<{ threadId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);

  const customerId = searchParams.get('customer_id') ?? '';
  const includeArchived = searchParams.get('archiviert') === '1';

  const setFilter = (next: { customer_id?: string; archiviert?: boolean }) => {
    const params = new URLSearchParams(searchParams);
    if (next.customer_id !== undefined) {
      if (next.customer_id) params.set('customer_id', next.customer_id);
      else params.delete('customer_id');
    }
    if (next.archiviert !== undefined) {
      if (next.archiviert) params.set('archiviert', '1');
      else params.delete('archiviert');
    }
    setSearchParams(params, { replace: true });
    setPage(1);
  };

  const { data: list, isLoading: listLoading } = useEmailThreads({
    page,
    perPage: PER_PAGE,
    customerId: customerId || undefined,
    includeArchived,
  });
  const { data: thread, isLoading: threadLoading } = useEmailThread(threadId ?? null);

  // Filter bleiben beim Wechsel zwischen Liste und Thread erhalten
  const query = searchParams.toString();
  const selectThread = (id: string) => navigate(`/emails/${id}${query ? `?${query}` : ''}`);
  const backToList = () => navigate(`/emails${query ? `?${query}` : ''}`);

  const pages = list?.pages ?? 1;
  const kundenSuche = useAuswahlSuche('customer', customerId);

  return (
    <div>
      <CrmPageHeader
        title="E-Mails"
        subtitle={list ? `${list.total} ${list.total === 1 ? 'Thread' : 'Threads'}` : 'Synchronisierte Korrespondenz'}
      >
        {/* Filter — auf kleinen Schirmen nur, solange kein Thread offen ist */}
        <div className={`flex w-full flex-wrap items-center gap-3 pb-4 ${threadId ? 'hidden lg:flex' : ''}`}>
          <Combobox
            {...kundenSuche}
            aria-label="Nach Kunde filtern"
            value={customerId}
            onChange={(id) => setFilter({ customer_id: id })}
            placeholder="Alle Kunden"
            searchPlaceholder="Kunde suchen"
            className="w-72 max-w-full"
          />
          <NativeCheckbox
            checked={includeArchived}
            onChange={(e) => setFilter({ archiviert: e.target.checked })}
            label="Archivierte anzeigen"
          />
        </div>
      </CrmPageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Liste — auf kleinen Schirmen nur, solange kein Thread offen ist */}
        <Card flush className={`overflow-hidden lg:col-span-2 ${threadId ? 'hidden lg:block' : ''}`}>
          <EmailThreadList
            threads={list?.items ?? []}
            isLoading={listLoading}
            onSelectThread={selectThread}
            selectedThreadId={threadId ?? null}
          />
          {pages > 1 && (
            <div className="border-t border-border px-4 pb-3">
              <Pagination page={page} pageCount={pages} onPageChange={setPage} />
            </div>
          )}
        </Card>

        {/* Detail */}
        <Card className={`lg:col-span-3 ${threadId ? '' : 'hidden lg:block'}`}>
          {threadId ? (
            <EmailThreadDetail thread={thread ?? null} isLoading={threadLoading} onBack={backToList} />
          ) : (
            <EmptyState icon={Mail} title="Kein Thread gewählt" description="Nachrichten und Anhänge des gewählten Threads erscheinen hier." />
          )}
        </Card>
      </div>
    </div>
  );
}
